import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Col } from "@amzn/stencil-react-components/layout";
import { H2, H4, H5 } from "@amzn/stencil-react-components/text";
import * as Cookie from "js-cookie";
import { useChecklistArb } from "../../../hooks/use-arb";
import { getSteps, isCandidateAlreadyDownloadNecessaryDocUpload } from "../../../helpers/utils";
import { useAppDispatch, useAppSelector } from "../../../reduxStore/reduxHooks";
import {
  getDownloadableDocs,
  getIsCountryWithDownloadableContentDocUpload,
  setDownloadableDocs,
  toggleSubmitButton,
} from "../../../reduxStore/slices/docUploadSlice";
import { DownloadNecessaryDocUploadItem } from "../../../utility/types/common";
import { getLocalStorageData, setLocalStorageData } from "../../../helpers/localStorage";
import { LOCAL_STORAGE_KEY } from "../../../utility/enums/common";
import { useGetNecessaryDocUploadQuery } from "../../../reduxStore/api/apiSlice";
import DownloadDocumentItem from "./DownloadDocumentItem";
import AppLoader from "../../common/AppLoader";
import { BannerMessage } from "../../common/BannerMessage";
import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";

const DownloadDocuments = () => {
  const { docs: documents, showErrorBanner } = useAppSelector(getDownloadableDocs);
  const { applicationId } = useParams<{ applicationId: string }>();
  const dispatch = useAppDispatch();
  const bundle = useChecklistArb();
  const isCountryWithDownloadableContent = useAppSelector(getIsCountryWithDownloadableContentDocUpload);

  const { isFetching, data: downloadableDocsFromServer } = useGetNecessaryDocUploadQuery({
    applicationId,
    isEligibleToFetch: isCountryWithDownloadableContent,
  });

  useEffect(() => {
    const downloadedDocsFromLocalStorage = getLocalStorageData(
      LOCAL_STORAGE_KEY.HVH_ASH_DOWNLOADED_NECESSARY_DOC_UPLOAD
    );
    downloadableDocsFromServer &&
      dispatch(
        setDownloadableDocs({
          downloadableDocs: downloadableDocsFromServer.map((doc) => ({
            ...doc,
            downloadedDate: downloadedDocsFromLocalStorage
              ? downloadedDocsFromLocalStorage.filter(
                  (docFromLc: Partial<DownloadNecessaryDocUploadItem>) => docFromLc.downloadedDate
                )[0].downloadedDate
              : doc.downloadedDate,
            isDownloadedAtCurrentSession: false,
          })),
          showErrorBanner: false,
        })
      );
  }, [isFetching, downloadableDocsFromServer]);

  useEffect(() => {
    if (isCandidateAlreadyDownloadNecessaryDocUpload(applicationId)) {
      dispatch(toggleSubmitButton({ isEnabled: true }));
    }
  }, [applicationId]);

  const downloadFromLinkHandler = (downloadedDoc: DownloadNecessaryDocUploadItem) => {
    const downloadableDocsToLocalStorage: Partial<DownloadNecessaryDocUploadItem>[] = [];

    const downloadableDocs = documents.map((doc) => {
      if (doc.documentId === downloadedDoc.documentId) {
        downloadableDocsToLocalStorage.push({
          documentName: doc.documentName,
          documentId: doc.documentId,
          downloadedDate: new Date(),
        });

        return { ...doc, isDownloadedAtCurrentSession: true, downloadedDate: new Date(), errorMessage: "" };
      }

      if (doc.downloadedDate) {
        downloadableDocsToLocalStorage.push({
          documentName: doc.documentName,
          documentId: doc.documentId,
          downloadedDate: doc.downloadedDate,
        });
      }

      return doc;
    });

    setLocalStorageData(LOCAL_STORAGE_KEY.HVH_ASH_DOWNLOADED_NECESSARY_DOC_UPLOAD, downloadableDocsToLocalStorage);

    dispatch(setDownloadableDocs({ downloadableDocs, showErrorBanner: false }));
  };

  const renderSteps = <H5>{getSteps({ bundle, currentStep: 1, maxSteps: 2 })}</H5>;

  const renderBanner = () => {
    const isAllFileBeenDownloadedInCurrentSession =
      documents.filter((doc) => doc.isDownloadedAtCurrentSession).length === documents.length;

    if (isAllFileBeenDownloadedInCurrentSession) {
      return (
        <BannerMessage
          banner={{
            text: bundle.getMessage(
              "Checklist-docUpload-downloadPage-banner-success",
              "Successfully downloaded two documents. Click on ‘view documents’, open the documents, and save them to your device. View documents"
            ),
            isDismissible: true,
            type: MessageBannerType.Success,
          }}
        />
      );
    } else if (showErrorBanner) {
      return (
        <BannerMessage
          banner={{
            text: bundle.getMessage(
              "Checklist-docUpload-downloadPage-banner-error",
              "You did not download the documents. In oder to continue, download the documents below"
            ),
            isDismissible: true,
            type: MessageBannerType.Error,
          }}
        />
      );
    }

    return null;
  };

  if (isFetching) return <AppLoader />;

  return (
    <Col padding="S300" gridGap="S300">
      {renderBanner()}
      {renderSteps}
      <H4>
        {bundle.getMessage("Checklist-docUpload-downloadPage-header", `Download and fill out the documents below`)}
      </H4>
      <H5 fontWeight={"regular"}>
        {bundle.getMessage(
          "Checklist-docUpload-downloadPage-description",
          "Once filled out, you will upload these documents in the next step."
        )}
        {documents.map((doc) => (
          <DownloadDocumentItem key={doc.documentId} doc={doc} handleDownloadFromLink={downloadFromLinkHandler} />
        ))}
      </H5>
    </Col>
  );
};

export default DownloadDocuments;
